import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import Crop, { CropsWithCategoryProps } from '@dehaat/kisan-app-bl/models/CropV2';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useRef, useState } from 'react';
import CropCategoryAccordion from '@/components/CropCategoryAccordion';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import LazyImage from '@/components/LazyImage';
import Paragraph, { VARIANT } from '@/components/Paragraph';
import SearchBox from '@/components/SearchBox';
import useDebounce from '@/hooks/useDebounce';
import Spinner from './Spinner';
interface Props {
  show: boolean;
  currentCrops: Crop[];
  allCrops: CropsWithCategoryProps[];
  onContinue: (crops: Crop[]) => void;
  onBack?: VoidFunction;
  isSingleSelect?: boolean;
  heading?: string;
  isLoading?: boolean;
}
const CropSelectionModal = ({
  show,
  currentCrops,
  allCrops,
  onContinue,
  onBack,
  isSingleSelect = false,
  heading,
  isLoading = false
}: Props) => {
  const {
    t
  } = useTranslation('crop');
  const {
    locale
  } = useRouter();
  const cropsRef = useRef<Crop[]>([]);
  const [selectedCrops, setSelectedCrops] = useState<Crop[]>(currentCrops);
  const [filteredCrops, setFilteredCrops] = useState<Crop[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const preventClick = selectedCrops.length === 0;
  const handleCropSelect = (crop: Crop) => {
    if (isSearching) {
      setIsSearching(false);
    }
    if (isSingleSelect) {
      setSelectedCrops([crop]);
    } else {
      const isCrop = selectedCrops.some(({
        id
      }) => id === crop.id);
      if (isCrop) {
        setSelectedCrops(prev => prev.filter(c => c.id !== crop.id));
      } else {
        setSelectedCrops(prev => prev.concat(crop));
      }
    }
  };
  const handleSearch = useCallback((newSearchQuery: string) => {
    if (newSearchQuery) {
      const newFilteredCrops: Crop[] = [];
      const searchQueryRegex = new RegExp(newSearchQuery, 'i');
      cropsRef.current.forEach(crop => {
        if (searchQueryRegex.test(crop.name) || searchQueryRegex.test(crop.app_display_name)) {
          newFilteredCrops.push(crop);
        }
      });
      setFilteredCrops(newFilteredCrops);
    } else {
      setFilteredCrops([]);
    }
  }, []);
  const debouncedSearch = useDebounce(handleSearch, 500);
  useEffect(() => {
    if (isSearching) document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isSearching]);
  useEffect(() => {
    let crops: Crop[] = [];
    allCrops.forEach(category => crops = category.crops.reduce((prev, crop) => prev.concat(crop), crops));
    cropsRef.current = crops;
  }, [allCrops]);
  return <>
      {show ? <div className="top-0 left-0 z-50 fixed bg-white pb-16 w-full h-screen overflow-y-scroll">
          <HeaderWithBackButton showHomeIcon={false} showProfileIcon={false} heading={t('select_crops')} onBackClick={onBack} />

          {isLoading && <Spinner />}
          <section className="m-auto p-4 pt-18 pb-16 w-full max-w-7xl">
            {heading ? <h3 className="mt-4 font-nato-semibold text-base text-neutral-80">
                {heading}
              </h3> : null}
            <div className={`bg-white pt-6 ${isSearching ? 'h-screen w-full z-50' : ''}`}>
              <SearchBox lang={locale || DEFAULT_LANG} onBackClick={() => setIsSearching(false)} onFocus={() => setIsSearching(true)} onSearch={debouncedSearch} placeholder={t('type_crop_name')} showBackBtn={isSearching} />

              {isSearching && filteredCrops.length > 0 && <section className="px-2 py-4" data-testid="suggestion-section">
                  <Paragraph className="text-neutral-50" isHighlighted>
                    {t('suggestions')}
                  </Paragraph>
                  {filteredCrops.map(crop => <button className="flex items-center gap-2 mt-4" key={crop.id} data-testid={`suggestion-crop-${crop.id}`} onClick={() => handleCropSelect(crop)}>
                      <div className="relative border-neutral-40 border rounded-full w-10 h-10 overflow-hidden">
                        <LazyImage alt={crop.app_display_name} fallBackIcon={<div className="flex justify-center items-center bg-neutral-300 rounded-full w-10 h-10 text-2xl overflow-hidden">
                              <span>{crop.app_display_name[0]}</span>
                            </div>} imageUrl={crop.image_url} className="rounded-full w-10 h-10" />
                      </div>
                      <Paragraph className="ml-2" variant={VARIANT.BIG}>
                        {crop.app_display_name}
                      </Paragraph>
                    </button>)}
                </section>}
            </div>
            {allCrops.map(cropsWithCategory => <CropCategoryAccordion key={cropsWithCategory.category} cropCategory={cropsWithCategory} crops={selectedCrops.map(crop => crop.id)} onClick={handleCropSelect} />)}

            <footer className="right-0 bottom-0 left-0 z-10 fixed border-neutral-30 bg-white p-1 border-t">
              <button onClick={() => onContinue(selectedCrops)} disabled={preventClick} className={`cursor-pointer py-4 m-5 ${preventClick ? 'bg-neutral-30' : 'bg-primary-100'} rounded-lg text-white text-center font-nato-semibold w-webkit-fill`} data-testid="continue-btn">
                {'save'}
              </button>
            </footer>
          </section>
        </div> : null}
    </>;
};
export default CropSelectionModal;